import React from 'react'
import { UseFormRegister, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { US_STATES } from '../../../../site/site-constants'
import { StorageFormValues } from '../config/validationSchema'

interface VehicleInfoStepProps {
  register: UseFormRegister<StorageFormValues>
  errors: FieldErrors<StorageFormValues>
  setValue: UseFormSetValue<StorageFormValues>
}

const VehicleInfoStep: React.FC<VehicleInfoStepProps> = ({
  register,
  errors,
  setValue
}) => {
  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <label className="forceBlack" htmlFor="vehicle_type">Vehicle Type</label>
        <select
          id="vehicle_type"
          {...register('vehicle_type')}
          style={{ width: "100%", height: 36, borderRadius: 4, color: '#232043' }}
          required
        >
          <option value="">&nbsp;Select a Vehicle Type</option>
          <option value="travel-trailer">Travel Trailer</option>
          <option value="camping-trailer">Camping Trailer</option>
          <option value="utility-trailer">Utility Trailer</option>
          <option value="enclosed-trailer">Enclosed Trailer</option>
          <option value="motorhome">Motorhome</option>
          <option value="5th-wheel">5th Wheel</option>
          <option value="pop-up">Pop-up Camper</option>
          <option value="boat">Boat</option>
          <option value="van">Van</option>
          <option value="car">Car</option>
          <option value="pickup">Pickup</option>
          <option value="suv">SUV</option>
          <option value="large-bus">School Bus</option>
          <option value="box-truck">Box Truck</option>
          <option value="semi">Semi</option>
          <option value="food-truck">Food Truck</option>
          <option value="other">Other</option>
        </select>
        {errors.vehicle_type && <div className="input-feedback">{errors.vehicle_type.message}</div>}
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label className="forceBlack">Year & Make</label>
        <div style={{ display: 'flex', width: '100%', border: '1px solid #232043', borderRadius: '4px' }}>
          <select
            id="vehicle_year"
            {...register('vehicle_year')}
            style={{
              width: '30%',
              minWidth: '100px',
              height: 38,
              borderRadius: '4px 0 0 4px',
              border: 'none',
              borderRight: '1px solid #232043',
              backgroundColor: 'white',
              color: '#232043',
              fontSize: '16px',
              padding: '0.5rem'
            }}
            required
          >
            <option key="--" value="">Year</option>
            {Array.from({ length: 35 }, (_, index) => {
              const yearValue = (new Date()).getFullYear() - index;
              return (
                <option key={yearValue} value={yearValue}>{yearValue}</option>
              )
            })}
          </select>
          <input
            id="vehicle_make"
            type="text"
            placeholder="Vehicle Make"
            {...register('vehicle_make')}
            style={{
              width: '70%',
              height: 38,
              borderRadius: '0 4px 4px 0',
              border: 'none',
              padding: '0.5rem',
              fontSize: '16px',
              color: '#232043'
            }}
            className={errors.vehicle_make ? 'error' : ''}
            required
          />
        </div>
        {errors.vehicle_year && <div className="input-feedback">{errors.vehicle_year.message}</div>}
        {errors.vehicle_make && <div className="input-feedback">{errors.vehicle_make.message}</div>}
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="vehicle_model" className="forceBlack">Model</label>
        <input
          id="vehicle_model"
          placeholder="Vehicle Model"
          type="text"
          {...register('vehicle_model')}
          className={errors.vehicle_model ? 'text-input error' : 'text-input'}
        />
        {errors.vehicle_model && <div className="input-feedback">{errors.vehicle_model.message}</div>}
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="vin" className="forceBlack">VIN Number</label>
        <input
          id="vin"
          placeholder="Vehicle VIN #"
          type="text"
          {...register('vin')}
          className={errors.vin ? 'text-input error' : 'text-input'}
          required
        />
        {errors.vin && <div className="input-feedback">{errors.vin.message}</div>}
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label className="forceBlack">License Plate State & Number</label>
        <div style={{ display: 'flex', width: '100%', border: '1px solid #232043', borderRadius: '4px' }}>
          <select
            id="vehicle_lic_state"
            {...register('vehicle_lic_state')}
            style={{
              width: '30%',
              minWidth: '100px',
              height: 38,
              borderRadius: '4px 0 0 4px',
              border: 'none',
              borderRight: '1px solid #232043',
              backgroundColor: 'white',
              color: '#232043',
              fontSize: '16px',
              padding: '0.5rem'
            }}
            required
          >
            <option value="">State</option>
            {Object.keys(US_STATES).map((stCode: string) => (
              <option key={stCode} value={stCode}>{stCode}</option>
            ))}
          </select>
          <input
            id="vehicle_lic_number"
            placeholder="Plate Number"
            type="text"
            maxLength={12}
            {...register('vehicle_lic_number')}
            style={{
              width: '70%',
              height: 38,
              borderRadius: '0 4px 4px 0',
              border: 'none',
              padding: '0.5rem',
              fontSize: '16px',
              color: '#232043'
            }}
            className={errors.vehicle_lic_number ? 'error' : ''}
            required
          />
        </div>
        {errors.vehicle_lic_state && <div className="input-feedback">{errors.vehicle_lic_state.message}</div>}
        {errors.vehicle_lic_number && <div className="input-feedback">{errors.vehicle_lic_number.message}</div>}
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label className="forceBlack" style={{ display: 'block', marginBottom: '0.5rem' }}>Proof Of Insurance</label>
        <input
          id="proof_of_insurance"
          name="proof_of_insurance"
          className="forceBlack"
          style={{ borderWidth: 1, borderColor: '#232043', width: '100%' }}
          type="file"
          accept="image/*,.pdf"
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              setValue('proof_of_insurance', e.target.files[0], { shouldValidate: true })
            }
          }}
          required
        />
        {errors.proof_of_insurance && <div className="input-feedback">{errors.proof_of_insurance.message as string}</div>}
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <label htmlFor="description" className="forceBlack">
          Vehicle Description <span style={{ fontSize: 14 }}>(optional)</span>
        </label>
        <input
          id="description"
          placeholder="Vehicle description"
          type="text"
          {...register('description')}
        />
      </div>
    </div>
  )
}

export default VehicleInfoStep
